import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { BasicListModel, RmsUser } from '../../shared/models';
import { AbstractCRUD, CRUDService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { UserService } from './user.service';
import { Domain } from '../../shared/models/domain.model';
import { CRMItem } from '../../shared/models/crm/CRMItem.model';
import { AuthenticationService, AuthorizationService } from '../services';

@Injectable({
  providedIn: 'root'
})
export class CrmUserService extends AbstractCRUD<BasicListModel> implements CRUDService<BasicListModel> {

  public static CRM_PM_ROLE = 'Sonar PM';
  public static CRM_USER_ROLE = 'Sonar User';

  private domainCache$: Observable<PaginatedList<Domain>>;
  private userCache$: Observable<Array<RmsUser>>;
  private diligenceLeaderCache$: Observable<Array<RmsUser>>;

  private userDomainsLeader: Array<Domain> = [];

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
    private userService: UserService,
    private authorizationService: AuthorizationService,
    private authenticationService: AuthenticationService,
  ) {
    super(exceptionService, http, utilsService);
    this.setUserDomainsLeader();
  }

  get domains() {
    if (!this.domainCache$) {
      this.domainCache$ = this.loadCrmUserDomains().pipe(shareReplay(1));
    }
    return this.domainCache$;
  }

  get users() {
    if (!this.userCache$) {
      this.userCache$ = this.loadCrmUsers().pipe(shareReplay(1));
    }
    return this.userCache$;
  }

  get diligenceLeaders() {
    if (!this.diligenceLeaderCache$) {
      this.diligenceLeaderCache$ = this.loadCrmDiligenceLeaders().pipe(shareReplay(1));
    }
    return this.diligenceLeaderCache$;
  }

  public isCrmUser(crmItem?: CRMItem): boolean {
    return this.authorizationService.hasTenantRole([CrmUserService.CRM_USER_ROLE]);
  }

  public isDomainLead(crmItem?: CRMItem): boolean {
    if (crmItem) {
      return this.userDomainsLeader.some(udl => udl.id === crmItem.domains[0].id);
    }
    return this.userDomainsLeader.length > 0;
  }

  public isPortfolioManager(crmItem?: CRMItem): boolean {
    return this.authorizationService.hasTenantRole([CrmUserService.CRM_PM_ROLE]);
  }

  public getResourcePath(): string {
    return '/crm';
  }

  public getDomainsById(userId: number): Observable<Array<Domain>>  {
    return this.domains.pipe(map((dms) => dms.records.filter(d => d.users.some(u => u.id === userId))));
  }

  public autocompleteUsers(term: string): Observable<any> {
    return this.userService.autocompleteUsers(term).pipe(withLatestFrom(this.users), map(([res, users]) => {
      res.records = res.records.filter(rec => users.some(user => user.id === +rec.id));
      return res;
    }));
  }

  public loadCrmUsers(): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/user/users`)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public loadStructCapCrmUsers(): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/user/struct-cap-users`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }


  public loadCrmUserDomains(): Observable<any> {
    return super.getHTTP().get(`/api/rms/domains?page=0&pagesize=5000`)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }


  public loadCrmDiligenceLeaders(): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/user/diligenceleaders`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  private setUserDomainsLeader() {
    const currentUser = this.authenticationService.getLoggedUser();
    this.domains.subscribe(d => {
      this.userDomainsLeader = d.records.filter(dmns => dmns.users.filter(u => u.leader).map(u => u.id).some(u => u === currentUser.id));
    });
  }
}
